import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Wifi, Loader2, KeyRound, ArrowRight, Receipt, User, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

const RECEIPT_FALLBACK_CODES = new Set(["RECEIPT_NOT_FOUND", "RECEIPT_EXPIRED"]);

export default function CustomerLogin() {
  const nav = useNavigate();
  const { login } = useCustomerAuth();
  const [mode, setMode] = useState("sms"); // "sms" | "mpesa" | "account"
  const [step, setStep] = useState(1); // 1=phone, 2=otp
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [receipt, setReceipt] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [devCode, setDevCode] = useState("");

  const receiptLogin = async () => {
    if (!receipt.trim()) { toast.error("Enter your M-Pesa receipt code"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/customer/receipt-login", { receipt: receipt.trim() });
      login(data.token, data.customer);
      toast.success("Welcome back");
      nav("/customer/dashboard");
    } catch (e) {
      const code = e.response?.data?.code;
      toast.error(e.response?.data?.message || e.message);
      if (RECEIPT_FALLBACK_CODES.has(code)) setMode("sms");
    } finally { setBusy(false); }
  };

  const portalLogin = async () => {
    if (!accountNumber.trim() || !accountPassword) { toast.error("Enter your account number and password"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/customer/portal-login", {
        account_number: accountNumber.trim(), password: accountPassword,
      });
      login(data.token, data.customer);
      toast.success("Welcome back");
      nav("/customer/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const requestOtp = async () => {
    if (!phone || phone.replace(/\D/g, "").length < 9) { toast.error("Enter a valid phone"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/customer/otp/request", { phone });
      setStep(2);
      if (data.dev_code) setDevCode(data.dev_code);
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (!code || code.length < 4) { toast.error("Enter the code"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/customer/otp/verify", { phone, code });
      login(data.token, data.customer);
      toast.success("Welcome back");
      nav("/customer/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5] text-[#1A1C20]">
      <header className="border-b border-[#E1E4E8] bg-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center gap-3">
          <div className="h-9 w-9 grid place-items-center bg-[#43B02A] rounded-full">
            <Wifi className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading font-black text-lg leading-none">NetOnLain</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A] mt-1">My Account</div>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-6 py-16" data-testid="customer-login">
        <Link to="/" className="text-xs font-mono uppercase tracking-[0.2em] text-[#57606A] hover:text-[#43B02A]">
          ← Back to hotspot
        </Link>

        <div className="mt-8 flex border border-[#E1E4E8] bg-white" data-testid="login-mode-toggle">
          <button
            data-testid="login-mode-sms"
            onClick={() => setMode("sms")}
            className={`flex-1 h-11 font-mono text-xs uppercase tracking-[0.15em] font-bold ${mode === "sms" ? "bg-[#1A1C20] text-white" : "text-[#57606A]"}`}
          >SMS code</button>
          <button
            data-testid="login-mode-mpesa"
            onClick={() => setMode("mpesa")}
            className={`flex-1 h-11 font-mono text-xs uppercase tracking-[0.15em] font-bold ${mode === "mpesa" ? "bg-[#1A1C20] text-white" : "text-[#57606A]"}`}
          >M-Pesa code</button>
          <button
            data-testid="login-mode-account"
            onClick={() => setMode("account")}
            className={`flex-1 h-11 font-mono text-xs uppercase tracking-[0.15em] font-bold ${mode === "account" ? "bg-[#1A1C20] text-white" : "text-[#57606A]"}`}
          >Account login</button>
        </div>

        {mode === "account" ? (
          <div className="mt-6" data-testid="account-login-step">
            <h1 className="font-heading font-black text-4xl leading-[1.05]">Sign in with your account</h1>
            <p className="mt-3 text-[#57606A]">Use the account number and password your ISP gave you.</p>
            <div className="mt-8 space-y-4">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Account number</Label>
                <div className="mt-2 relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#57606A]" />
                  <Input
                    data-testid="account-number-input"
                    value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="e.g. NLN3"
                    className="pl-11 h-14 text-lg font-mono uppercase rounded-none border-[#E1E4E8]"
                  />
                </div>
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Password</Label>
                <div className="mt-2 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#57606A]" />
                  <Input
                    data-testid="account-password-input"
                    type="password" value={accountPassword} onChange={(e) => setAccountPassword(e.target.value)}
                    placeholder="Password"
                    className="pl-11 h-14 text-lg font-mono rounded-none border-[#E1E4E8]"
                    onKeyDown={(e) => { if (e.key === "Enter") portalLogin(); }}
                  />
                </div>
              </div>
              <Button data-testid="account-login-btn" onClick={portalLogin} disabled={busy}
                      className="w-full h-14 rounded-none bg-[#43B02A] hover:bg-[#368F22] text-white font-bold text-base">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>SIGN IN <ArrowRight className="h-5 w-5 ml-1" /></>}
              </Button>
            </div>
          </div>
        ) : mode === "mpesa" ? (
          <div className="mt-6" data-testid="mpesa-login-step">
            <h1 className="font-heading font-black text-4xl leading-[1.05]">Sign in with M-Pesa</h1>
            <p className="mt-3 text-[#57606A]">Enter the receipt code from any payment on your account.</p>
            <div className="mt-8 space-y-4">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">M-Pesa Receipt</Label>
                <div className="mt-2 relative">
                  <Receipt className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#57606A]" />
                  <Input
                    data-testid="receipt-login-input"
                    value={receipt} onChange={(e) => setReceipt(e.target.value.toUpperCase())}
                    placeholder="e.g. MABC123XYZ"
                    className="pl-11 h-14 text-lg font-mono uppercase tracking-widest rounded-none border-[#E1E4E8]"
                  />
                </div>
              </div>
              <Button data-testid="receipt-login-btn" onClick={receiptLogin} disabled={busy}
                      className="w-full h-14 rounded-none bg-[#43B02A] hover:bg-[#368F22] text-white font-bold text-base">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>SIGN IN <ArrowRight className="h-5 w-5 ml-1" /></>}
              </Button>
              <p className="text-xs text-[#57606A]">Works from any device, as long as that receipt's plan is still active.</p>
            </div>
          </div>
        ) : (<>

        <div className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-[#43B02A]">// STEP {step} OF 2</div>
        <h1 className="mt-2 font-heading font-black text-4xl leading-[1.05]">
          {step === 1 ? "Sign in with your phone" : "Enter the SMS code"}
        </h1>
        <p className="mt-3 text-[#57606A]">
          {step === 1 ? "We'll text you a 6-digit code — no password needed." : `Code sent to +${phone.replace(/\D/g, "")}`}
        </p>

        {step === 1 && (
          <div className="mt-8 space-y-4">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">Phone number</Label>
              <div className="mt-2 relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 font-mono font-bold text-[#43B02A]">+254</div>
                <Input
                  data-testid="customer-phone-input"
                  type="tel" inputMode="tel" placeholder="7XX XXX XXX"
                  value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="pl-16 h-14 text-lg font-mono rounded-none border-[#E1E4E8]"
                />
              </div>
            </div>
            <Button data-testid="request-otp-btn" onClick={requestOtp} disabled={busy}
                    className="w-full h-14 rounded-none bg-[#43B02A] hover:bg-[#368F22] text-white font-bold text-base">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>SEND CODE <ArrowRight className="h-5 w-5 ml-1" /></>}
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="mt-8 space-y-4" data-testid="otp-step">
            {devCode && (
              <div className="border-2 border-dashed border-[#43B02A] bg-[#43B02A]/5 p-3 font-mono text-xs" data-testid="dev-otp-hint">
                <span className="uppercase tracking-[0.2em] text-[#43B02A]">// DEV MODE — SMS mocked. Your code:</span>
                <div className="text-2xl font-bold mt-1 tracking-widest">{devCode}</div>
              </div>
            )}
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#57606A]">6-digit code</Label>
              <div className="mt-2 relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#57606A]" />
                <Input
                  data-testid="customer-otp-input"
                  type="text" inputMode="numeric" maxLength={6} placeholder="123456"
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="pl-11 h-14 text-2xl font-mono tracking-[0.5em] rounded-none border-[#E1E4E8] text-center"
                />
              </div>
            </div>
            <Button data-testid="verify-otp-btn" onClick={verifyOtp} disabled={busy}
                    className="w-full h-14 rounded-none bg-[#43B02A] hover:bg-[#368F22] text-white font-bold">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "VERIFY & SIGN IN"}
            </Button>
            <button onClick={() => setStep(1)} className="text-xs font-mono uppercase tracking-[0.2em] text-[#57606A] hover:text-[#43B02A]">
              ← Use a different number
            </button>
          </div>
        )}
        </>)}
      </main>
    </div>
  );
}
